import { DateTimeInput, Field, RadioGroup, TextOutput } from 'Components/Core';
import { withInjectedProps } from 'Components/Core/InjectedPropsContext';
import { ListFieldSection, SectionGroup } from 'Components/Layout';
import { IWithWorksheetInjectedProps, WorksheetEquipmentDropdown } from 'Components/Worksheets/Core';
import { MaterialDriedByType } from 'Components/Worksheets/Shared/types';
import { valueOrDefault } from 'Lib/Any';
import { DateTimeDataFormat } from 'Lib/Format';
import { pipe } from 'Lib/Hooks/Selectors';
import deepEquals from 'Lib/Object/deepEquals';
import React from 'react';
import { useSelector } from 'react-redux';
import {
  OversizeMaterialRemovedByType,
  OversizeSieveApertureSizeType,
  PasteWaterContentEquilibriumType,
} from '../../../types';

let BsEnIsoPreparationTestPortionSection: React.FC<IWithWorksheetInjectedProps> = ({ createWorksheetSelector }) => {
  const oversizeSieveApertureSize = useSelector(createWorksheetSelector!('OversizeSieveApertureSize'), deepEquals) as
    | OversizeSieveApertureSizeType
    | undefined;

  const oversizeMaterialRemovedBy = useSelector(
    createWorksheetSelector!('OversizeMaterialRemovedBy'),
    deepEquals
  ) as OversizeMaterialRemovedByType;

  const noOversizeMaterial = useSelector(pipe(createWorksheetSelector!('NoOversizeMaterial'), valueOrDefault(false)));

  const materialDriedBy = useSelector(createWorksheetSelector!('MaterialDriedBy'), deepEquals) as MaterialDriedByType;

  const pasteWaterContentEquilibrium = useSelector(
    createWorksheetSelector!('PasteWaterContentEquilibrium'),
    deepEquals
  ) as PasteWaterContentEquilibriumType;

  const asReceivedWaterContentDetermined = useSelector(
    pipe(createWorksheetSelector!('AsReceivedWaterContentDetermined'), valueOrDefault(false))
  );

  let materialPercentLabel = '';
  switch (oversizeSieveApertureSize) {
    case OversizeSieveApertureSizeType.Sieve0_400mm:
      materialPercentLabel = 'Material < 0.4mm, K';
      break;
    case OversizeSieveApertureSizeType.Sieve0_425mm:
      materialPercentLabel = 'Material < 0.425mm, K';
      break;
  }

  return (
    <SectionGroup heading="Test Portion">
      <ListFieldSection>
        {oversizeMaterialRemovedBy === OversizeMaterialRemovedByType.WetSieving && !noOversizeMaterial && (
          <>
            <Field
              input={<RadioGroup />}
              name="MaterialDriedBy"
              options={[
                { label: 'Air', value: MaterialDriedByType.Air },
                { label: 'Oven (50°C)', value: MaterialDriedByType.Oven },
              ]}
            />
            <Field
              input={<WorksheetEquipmentDropdown />}
              equipmentType="oven"
              name="OvenForRetainedMaterial"
              visible={materialDriedBy === MaterialDriedByType.Oven}
            />
          </>
        )}

        <Field input={<TextOutput />} name="MaterialPercent" label={materialPercentLabel} />

        {(!noOversizeMaterial || asReceivedWaterContentDetermined) && (
          <Field input={<TextOutput />} name="EquivalentWaterContent" />
        )}

        {!(oversizeMaterialRemovedBy === OversizeMaterialRemovedByType.WetSieving && !noOversizeMaterial) && (
          <>
            <Field
              input={<RadioGroup />}
              name="PasteWaterContentEquilibrium"
              options={[
                { label: 'Not Required', value: PasteWaterContentEquilibriumType.NotRequired },
                { label: 'Air Drying', value: PasteWaterContentEquilibriumType.AirDrying },
                { label: 'Soaking', value: PasteWaterContentEquilibriumType.Soaking },
              ]}
            />

            {pasteWaterContentEquilibrium === PasteWaterContentEquilibriumType.Soaking && (
              <>
                <Field
                  input={<DateTimeInput />}
                  name="SoakingStartDateTime"
                  dataFormat={DateTimeDataFormat.UnspecifiedLocalDateTime}
                />
                <Field
                  input={<DateTimeInput />}
                  name="SoakingEndDateTime"
                  dataFormat={DateTimeDataFormat.UnspecifiedLocalDateTime}
                />
                <Field input={<TextOutput />} name="SoakingPeriod" />
              </>
            )}
          </>
        )}
      </ListFieldSection>
    </SectionGroup>
  );
};

BsEnIsoPreparationTestPortionSection = withInjectedProps(BsEnIsoPreparationTestPortionSection);

export { BsEnIsoPreparationTestPortionSection };
export default BsEnIsoPreparationTestPortionSection;
